// components/Sidebar.tsx
'use client'

import { usePathname } from 'next/navigation'
import {
  LayoutDashboard,
  Car,
  User as UserIcon,
  Briefcase,
  CirclePlus,
  Settings,
  ChevronRight,
  Shield,
  LogOut,
  User
} from 'lucide-react'
import { JSX, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { User as UserType } from '@/types/auth'
import Image from 'next/image';

interface NavItem {
  icon: JSX.Element;
  label: string;
  href: string;
  active: boolean;
}

interface QuickAction {
  icon: JSX.Element;
  label: string;
  href: string;
  color: string;
}

interface BottomItem {
  icon: JSX.Element;
  label: string;
  href: string;
  active: boolean;
}

const Sidebar = () => {
  const pathname = usePathname()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const { user, logout, hasRole } = useAuth()
  
  const navItems: NavItem[] = [
    { 
      icon: <LayoutDashboard size={22} />, 
      label: 'Tableau de bord', 
      href: '/',
      active: pathname === '/'
    },
    { 
      icon: <Car size={22} />, 
      label: 'Véhicule', 
      href: '/vehicles',
      active: pathname === '/vehicles'
    },
    { 
      icon: <UserIcon size={22} />, 
      label: 'Chauffeurs', 
      href: '/drivers',
      active: pathname === '/drivers'
    },
    { 
      icon: <Briefcase size={22} />, 
      label: 'Propriétaires', 
      href: '/owners',
      active: pathname === '/owners'
    },
  ]

  const quickActions: QuickAction[] = [
    { 
      icon: <CirclePlus size={22} />, 
      label: 'Nouveau véhicule', 
      href: '/new-vehicle',
      color: 'hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200' 
    },
  ]

  const bottomItems: BottomItem[] = [
    // { 
    //   icon: <Settings size={22} />, 
    //   label: 'Settings', 
    //   href: '/settings',
    //   active: false 
    // },
  ]

  // Only show Admin section for super_admin users
  if (hasRole('super_admin')) {
    bottomItems.push({
      icon: <Shield size={22} />,
      label: 'Admin',
      href: '/admin',
      active: false
    });
  }

  const handleLogout = async (): Promise<void> => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setShowLogoutConfirm(false)
    }
  }

  return (
    <>
      <aside className="w-[72px] sm:w-64 flex-shrink-0 bg-white flex flex-col transition-all duration-300 z-20 border-r border-slate-100 shadow-sm">
        {/* Logo */}
        <div className="h-16 flex items-center justify-center sm:justify-start sm:px-6 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-primary/10">
          <Image
              src="/CUY.jpg"
              alt="Urban Council Logo"
              width={70}
              height={70}
              className="object-cover"
              priority
            />
          <div className="ml-3 hidden sm:block">
            <span className="font-bold text-slate-800 tracking-wide truncate block">Conseil urbain</span>
            <span className="text-xs text-slate-500 font-medium">Admin Taxi</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col p-4 gap-1 overflow-y-auto no-scrollbar">
          <div className="px-2 mb-2 hidden sm:block">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Menu principal
            </h4>
          </div>
          
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`group relative flex items-center gap-3 px-3 py-3 rounded-xl justify-center sm:justify-start transition-all duration-300 ${
                item.active
                  ? 'bg-gradient-to-r from-primary/10 to-primary/5 text-primary border-r-4 border-primary shadow-sm'
                  : 'text-slate-600 hover:text-primary hover:bg-slate-50 hover:shadow-sm'
              }`}
            >
              {item.active && (
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full"></div>
              )}
              
              <span className={`relative transition-all duration-300 ${
                item.active 
                  ? 'scale-110 text-primary' 
                  : 'group-hover:scale-110 text-slate-500 group-hover:text-primary'
              }`}>
                {item.icon}
                {item.active && (
                  <span className="absolute -top-1 -right-1 size-2 bg-primary rounded-full animate-pulse"></span>
                )}
              </span>
              
              <span className={`hidden sm:block font-medium text-sm transition-all duration-300 ${
                item.active ? 'font-semibold text-primary' : 'text-slate-700 group-hover:text-primary'
              }`}>
                {item.label}
              </span>
            </a>
          ))}

          {/* Quick Actions */}
          <div className="mt-6 px-2 mb-2 hidden sm:block">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
             Actions rapides
            </h4>
          </div>

          {quickActions.map((action) => (
            <a
              key={action.label}
              href={action.href}
              className={`group flex items-center gap-3 px-3 py-3 rounded-xl justify-center sm:justify-start transition-all duration-300 border border-slate-100 ${action.color} hover:shadow-sm`}
            >
              <span className="transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                {action.icon}
              </span>
              <span className="hidden sm:block font-medium text-sm group-hover:font-semibold">
                {action.label}
              </span>
            </a>
          ))}

          <div className="my-4 border-t border-slate-100 mx-2"></div>

          {/* Bottom Items */}
          {bottomItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="group flex items-center gap-3 px-3 py-3 rounded-xl text-slate-600 hover:text-primary hover:bg-slate-50 transition-all duration-300 justify-center sm:justify-start hover:shadow-sm"
            >
              <span className="transition-all duration-300 group-hover:scale-110">
                {item.icon}
              </span>
              <span className="hidden sm:block font-medium text-sm group-hover:font-semibold">
                {item.label}
              </span>
            </a>
          ))}

          {/* Logout Button */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="group flex items-center gap-3 px-3 py-3 rounded-xl text-slate-600 hover:text-red-600 hover:bg-red-50 transition-all duration-300 justify-center sm:justify-start hover:shadow-sm mt-2"
          >
            <span className="transition-all duration-300 group-hover:scale-110">
              <LogOut size={22} />
            </span>
            <span className="hidden sm:block font-medium text-sm group-hover:font-semibold">
              Déconnexion
            </span>
          </button>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-100 bg-gradient-to-t from-white to-slate-50">
          <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white transition-all duration-300 justify-center sm:justify-start hover:shadow-sm border border-slate-100 hover:border-primary/20 group">
            <div className="relative">
              <div className="size-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white shrink-0 shadow-md shadow-primary/30">
                <User size={20} />
              </div>
              <div className="absolute -bottom-1 -right-1 size-3 bg-emerald-500 rounded-full border-2 border-white"></div>
            </div>
            <div className="hidden sm:flex flex-col items-start overflow-hidden flex-1">
              <p className="text-sm font-bold text-slate-800 truncate w-full group-hover:text-primary transition-colors">
                {user?.name || 'User'}
              </p>
             <p className="text-xs text-slate-500 truncate w-full flex items-center group-hover:text-primary/70 transition-colors capitalize">
  {(user?.role || 'Administrator').replace(/_/g, ' ')}
</p>

            </div>
            <ChevronRight size={16} className="hidden sm:block text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </button>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                  <LogOut size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Confirmer la déconnexion</h3>
                  <p className="text-sm text-slate-500">Êtes-vous sûr de vouloir vous déconnecter ?</p>
                </div>
              </div>

              <p className="text-slate-600 mb-6">
                Vous serez redirigé vers la page de connexion et devrez saisir à nouveau vos identifiants pour accéder au panneau d'administration.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-3 text-sm font-semibold"
                >
                  Oui, se déconnecter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Sidebar